export type ActorRole = 'owner' | 'admin' | 'analyst' | 'member' | 'viewer';

export type NavItem = {
  to: string;
  label: string;
  icon: string;
  roles?: readonly ActorRole[];
};

export type RouteTrailItem = { label: string; to?: string };

export const EDITOR_ROLES = ['owner', 'admin', 'analyst', 'member'] as const satisfies readonly ActorRole[];
export const ADMIN_ROLES = ['owner', 'admin'] as const satisfies readonly ActorRole[];

export const PRODUCT_NAV: NavItem[] = [
  { to: '/', label: 'Главная', icon: '⌘' },
  { to: '/tours', label: 'Каталог', icon: '▤' },
  { to: '/flights', label: 'Перелёты', icon: '⌁' },
  { to: '/departures', label: 'Пакетные туры', icon: '♧' },
  { to: '/market', label: 'Рынок', icon: '◎' },
  { to: '/compare', label: 'Сравнения', icon: '⇄' },
  { to: '/decision-history', label: 'История решений', icon: '◴' },
];

export const AUTOMATION_NAV: NavItem[] = [
  { to: '/search-profiles', label: 'Профили поиска', icon: '⌖', roles: EDITOR_ROLES },
  { to: '/signal-rules', label: 'Правила сигналов', icon: '⚡', roles: EDITOR_ROLES },
  { to: '/notifications', label: 'Уведомления', icon: '♧' },
  { to: '/watch', label: 'Наблюдения', icon: '⚙', roles: EDITOR_ROLES },
];

export const DATA_NAV: NavItem[] = [
  { to: '/events', label: 'Сигналы', icon: '♧' },
  { to: '/data', label: 'Центр данных', icon: '▦' },
  { to: '/operators', label: 'Операторы', icon: '▥' },
  { to: '/quality', label: 'Качество', icon: '◇' },
  { to: '/pages', label: 'Монитор страниц', icon: '•••', roles: EDITOR_ROLES },
];

export const WORKSPACE_NAV: NavItem[] = [
  { to: '/workspace', label: 'Организация и команда', icon: '◫' },
  { to: '/data#workspace-usage', label: 'Использование', icon: '◴' },
  { to: '/data#audit', label: 'Аудит', icon: '✓', roles: ADMIN_ROLES },
];

export const ACCOUNT_NAV: NavItem[] = [
  { to: '/account/profile', label: 'Профиль', icon: '◉' },
  { to: '/account/security', label: 'Безопасность', icon: '◇' },
  { to: '/account/security#sessions', label: 'Сессии', icon: '⌁' },
];

export const DECISION_NAV: NavItem[] = [
  { to: '/departures#next-7-days', label: 'Ближайшие вылеты', icon: '◷' },
  { to: '/tours?kind=tour&mode=discounts&sort=drop', label: 'Максимальные скидки', icon: '↘' },
  { to: '/notifications?state=unread', label: 'Новые уведомления', icon: '!' },
  { to: '/market#market-matrix', label: 'Сравнить рынок', icon: '⇄' },
];

export const ALL_NAV_ITEMS = [
  ...PRODUCT_NAV,
  ...AUTOMATION_NAV,
  ...DATA_NAV,
  ...WORKSPACE_NAV,
  ...ACCOUNT_NAV,
  ...DECISION_NAV,
];

export function canUseNavItem(item: NavItem, role: ActorRole | undefined): boolean {
  return !item.roles || (role !== undefined && item.roles.includes(role));
}

export function routeRoles(pathname: string): readonly ActorRole[] | undefined {
  if (pathname === '/search-profiles' || pathname === '/signal-rules' || pathname === '/watch') {
    return EDITOR_ROLES;
  }
  if (pathname === '/pages') return EDITOR_ROLES;
  return undefined;
}

export function canOpenRoute(pathname: string, role: ActorRole): boolean {
  const roles = routeRoles(pathname);
  return roles ? roles.includes(role) : true;
}

export function routeTrail(pathname: string, hash = ''): RouteTrailItem[] {
  if (pathname === '/') return [];
  if (pathname.startsWith('/stay/')) {
    return [{ label: 'Каталог', to: '/tours' }, { label: 'Анализ предложения' }];
  }
  if (pathname === '/data' && hash === '#workspace-usage') {
    return [{ label: 'Центр данных', to: '/data' }, { label: 'Использование' }];
  }
  if (pathname === '/data' && hash === '#audit') {
    return [{ label: 'Центр данных', to: '/data' }, { label: 'Аудит' }];
  }
  if (pathname === '/data' && hash === '#context-history') {
    return [{ label: 'Центр данных', to: '/data' }, { label: 'Глубина истории' }];
  }
  const match = ALL_NAV_ITEMS.find((item) => item.to.split(/[?#]/, 1)[0] === pathname);
  if (!match) return [{ label: 'Раздел' }];
  const group = PRODUCT_NAV.includes(match)
    ? 'Продукты'
    : AUTOMATION_NAV.includes(match)
      ? 'Автоматизация'
      : DATA_NAV.includes(match)
        ? 'Данные и доверие'
        : WORKSPACE_NAV.includes(match)
          ? 'Рабочее пространство'
          : 'Аккаунт';
  return [{ label: group }, { label: match.label }];
}

export function fallbackRoute(pathname: string): string {
  if (pathname.startsWith('/stay/')) return '/tours';
  return '/';
}
